import React from 'react';
import { getPriceBreakdown } from '../../context/CartContext';
import { Minus, Plus, Trash2, CheckCircle2, Pencil, Truck, Percent } from 'lucide-react';

const PRINT_LOCATIONS = [
  { key: 'front', label: 'Front' },
  { key: 'back', label: 'Back' },
  { key: 'left', label: 'Left Sleeve' },
  { key: 'right', label: 'Right Sleeve' },
];

const PrintLocationsSummary = ({ currentDesigns }) => (
  <div className="bg-blue-50 rounded-xl p-3 border border-blue-100 mb-4">
    <p className="text-sm font-semibold text-blue-800 mb-2">Print Locations</p>
    <div className="grid grid-cols-4 gap-2">
      {PRINT_LOCATIONS.map(({ key, label }) => {
        const hasDesign = currentDesigns[key]?.length > 0;
        return (
          <div
            key={key}
            className={`flex flex-col items-center p-2 rounded-lg ${
              hasDesign ? 'bg-green-100 border border-green-300' : 'bg-gray-100 border border-gray-200'
            }`}
          >
            {hasDesign ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 mb-1" />
            ) : (
              <div className="w-5 h-5 rounded-full border-2 border-dashed border-gray-300 mb-1" />
            )}
            <span className={`text-[10px] font-medium text-center ${hasDesign ? 'text-green-700' : 'text-gray-500'}`}>
              {label}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

const OrderItemCard = ({ item, onEditItem, onRemoveItem, onUpdateQuantity }) => {
  const breakdown = getPriceBreakdown(item.designs);
  return (
    <div
      className="bg-white rounded-xl p-3 border border-gray-100 hover:border-primary/30 hover:shadow-md transition-all cursor-pointer group"
      onClick={() => onEditItem(item)}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex gap-2">
          <div className="w-10 h-10 rounded-lg border-2 relative" style={{ backgroundColor: item.color }}>
            <div className="absolute inset-0 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Pencil size={14} className="text-white" />
            </div>
          </div>
          <div>
            <p className="font-semibold text-sm">{item.colorName}</p>
            <p className="text-xs text-gray-500">Size: {item.size}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!item.isCurrent && (
            <button
              onClick={(e) => { e.stopPropagation(); onRemoveItem(item.id); }}
              className="text-gray-400 hover:text-red-500 p-1"
              title="Remove from cart"
            >
              <Trash2 size={14} />
            </button>
          )}
          {item.isCurrent ? (
            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Current</span>
          ) : (
            <span className="text-xs text-gray-400 group-hover:text-primary transition-colors">Click to edit</span>
          )}
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-2">
        {breakdown.slice(1).map((b) => b.label).join(', ') || 'Empty shirt'}
      </div>

      <div className="flex justify-between items-center pt-2 border-t border-gray-100">
        <div className="flex items-center gap-1.5">
          {!item.isCurrent && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onUpdateQuantity(item.id, item.quantity - 1); }}
                disabled={item.quantity <= 1}
                className="w-6 h-6 rounded-full border flex items-center justify-center text-xs disabled:opacity-50 hover:bg-gray-100"
              >
                <Minus size={12} />
              </button>
              <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
              <button
                onClick={(e) => { e.stopPropagation(); onUpdateQuantity(item.id, item.quantity + 1); }}
                className="w-6 h-6 rounded-full border flex items-center justify-center text-xs hover:bg-gray-100"
              >
                <Plus size={12} />
              </button>
            </>
          )}
          {item.isCurrent && <span className="text-sm text-gray-500">Qty: {item.quantity}</span>}
        </div>
        <span className="font-bold text-sm">${(item.unitPrice * item.quantity).toFixed(2)}</span>
      </div>
    </div>
  );
};

const PriceSummary = ({ orderTotals, totalPrice }) => (
  <div className="pt-3 border-t border-gray-200 space-y-2">
    {orderTotals.totalQuantity < 3 && (
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-3 mb-2 border border-purple-100">
        <p className="text-xs font-semibold text-purple-800">
          {orderTotals.totalQuantity < 2
            ? `Add ${2 - orderTotals.totalQuantity} more shirt for FREE shipping!`
            : `Add ${3 - orderTotals.totalQuantity} more shirt for 15% off!`}
        </p>
      </div>
    )}

    <div className="flex justify-between text-sm">
      <span>Subtotal</span>
      <span>${orderTotals.subtotal.toFixed(2)}</span>
    </div>

    {orderTotals.hasDiscount && (
      <div className="flex justify-between text-sm text-green-600">
        <span className="flex items-center gap-1">
          <Percent size={14} />
          {orderTotals.discountPercent}% Bundle Discount
        </span>
        <span>-${orderTotals.discountAmount.toFixed(2)}</span>
      </div>
    )}

    <div className="flex justify-between text-sm">
      <span className="flex items-center gap-1">
        <Truck size={14} />
        Shipping
      </span>
      {orderTotals.hasFreeShipping ? (
        <span className="text-green-600 font-medium">FREE</span>
      ) : (
        <span>${orderTotals.shipping.toFixed(2)}</span>
      )}
    </div>

    <div className="flex justify-between items-center pt-2 border-t">
      <span className="font-bold">Total</span>
      <span className="text-2xl font-black text-primary">${totalPrice.toFixed(2)}</span>
    </div>
  </div>
);

export const OrderItemsList = ({
  displayItems,
  currentDesigns,
  hasCurrentDesign,
  orderTotals,
  totalPrice,
  onEditItem,
  onRemoveItem,
  onUpdateQuantity,
}) => (
  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 space-y-3">
    <h3 className="font-bold text-sm text-gray-800">Order Items ({displayItems.length})</h3>

    {hasCurrentDesign && <PrintLocationsSummary currentDesigns={currentDesigns} />}

    {displayItems.length === 0 ? (
      <p className="text-sm text-gray-500 py-4 text-center">No items in your order</p>
    ) : (
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {displayItems.map((item) => (
          <OrderItemCard
            key={item.id}
            item={item}
            onEditItem={onEditItem}
            onRemoveItem={onRemoveItem}
            onUpdateQuantity={onUpdateQuantity}
          />
        ))}
      </div>
    )}

    <PriceSummary orderTotals={orderTotals} totalPrice={totalPrice} />
  </div>
);

export default OrderItemsList;
